import { useMemo, useState } from "react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CaretUpDown, Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/**
 * Combobox de proveedores con búsqueda por nombre / CUIT / email / teléfono.
 *
 * Props:
 *   proveedores: array de proveedores ya cargados
 *   value: id del proveedor seleccionado (string)
 *   onChange(id): callback con el nuevo id
 *   placeholder, testId
 */
export default function ProveedorCombobox({
  proveedores = [],
  value = "",
  onChange,
  placeholder = "Buscar proveedor por nombre, CUIT…",
  testId = "proveedor-combobox",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return proveedores.slice(0, 30);
    return proveedores.filter((p) => {
      const hay = [p.nombre, p.cuit, p.email, p.telefono, p.razon_social]
        .map((x) => (x || "").toString().toLowerCase())
        .join(" ");
      return hay.includes(q);
    }).slice(0, 30);
  }, [proveedores, query]);

  const selected = proveedores.find((p) => p.id === value);
  const selectedLabel = selected
    ? `${selected.nombre}${selected.cuit ? ` · ${selected.cuit}` : ""}`
    : "";

  return (
    <div data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal h-9 rounded-sm"
            data-testid={`${testId}-trigger`}
          >
            <span className={cn("truncate text-left", !selectedLabel && "text-muted-foreground")}>
              {selectedLabel || "Seleccionar…"}
            </span>
            <CaretUpDown size={14} className="ml-2 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[92vw] sm:w-[420px] sm:max-w-[420px] p-0"
          align="start"
          data-testid={`${testId}-popover`}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={placeholder}
              value={query}
              onValueChange={setQuery}
              data-testid={`${testId}-input`}
            />
            <CommandList>
              <CommandEmpty>Sin resultados.</CommandEmpty>
              <CommandGroup>
                {filtered.map((p) => {
                  const isSel = p.id === value;
                  return (
                    <CommandItem
                      key={p.id}
                      value={p.id}
                      onSelect={() => {
                        onChange?.(p.id);
                        setOpen(false);
                        setQuery("");
                      }}
                      data-testid={`${testId}-item-${p.id}`}
                    >
                      <Check size={14} className={cn("mr-2", isSel ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium truncate">{p.nombre}</span>
                        <span className="text-[11px] text-muted-foreground truncate">
                          {p.cuit || "sin CUIT"}
                          {p.condicion_iva ? ` · ${p.condicion_iva}` : ""}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
